import assert from 'assert';
import { Fragment } from 'react';
import { useRouter } from 'next/router';
import Avatar from '@savvy-kit/components/avatar';
import { Breadcrumb, BreadcrumbItem, BreadcrumbList, BreadcrumbSeparator } from '@savvy-kit/components/breadcrumb';
import { Spinner } from '@savvy-kit/components/spinner';
import { Text } from '@savvy-kit/foundations/text';
import { graphql } from 'src/gql';
import { formatInactiveBreadcrumb } from 'src/utils/breadcrumbs';

import { useQueryWithErrorLogging } from '@savvy/graphql-hooks/useQueryWithErrorLogging';

const CLIENT_QUERY = graphql(/* GraphQL */ `
  query ClientFromClientHeaderCard($id: ID!) {
    client(id: $id) {
      user {
        id
        firstName
        lastName
        email
        profilePicUrl
      }
    }
  }
`);

type ClientHeaderCardProps = {
  readonly clientId?: string;
  readonly breadcrumbItems?: React.ReactElement[];
};

function ClientHeaderCard({ clientId, breadcrumbItems }: ClientHeaderCardProps) {
  const router = useRouter();
  const { clientUserId } = router.query;
  assert(
    typeof clientUserId === 'string' || clientUserId === undefined,
    `clientUserId should be a string or undefined, but it is ${typeof clientUserId}`,
  );
  const { loading, error, data } = useQueryWithErrorLogging(CLIENT_QUERY, {
    variables: {
      id: clientId ?? clientUserId ?? '',
    },
  });

  const client = data?.client;

  const clientName = `${client?.user.firstName} ${client?.user.lastName}`;

  const clientNameBreadcrumb = formatInactiveBreadcrumb({
    title: clientName,
  });
  const breadcrumbsWithClientName = [clientNameBreadcrumb].concat(breadcrumbItems || []);
  return (
    <>
      {loading && <Spinner className="size-12" />}
      {error && `Error! ${error.message}`}
      {data && (
        <div className="flex h-16 items-center gap-4">
          <Avatar showFallbackIcon imageUrl={client?.user.profilePicUrl} />
          <div className="flex flex-col">
            <Breadcrumb aria-label="breadcrumb">
              <BreadcrumbList>
                {breadcrumbsWithClientName.map((item, index) => (
                  <Fragment key={item.key ?? index}>
                    {index > 0 && <BreadcrumbSeparator className="[&>svg]:size-5" />}
                    <BreadcrumbItem>{item}</BreadcrumbItem>
                  </Fragment>
                ))}
              </BreadcrumbList>
            </Breadcrumb>
            <Text color="secondary">{client?.user.email}</Text>
          </div>
        </div>
      )}
    </>
  );
}

export default ClientHeaderCard;
